import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, Mail, FileText, ExternalLink, Globe, Search, 
  Download, Eye, Calendar, User, ChevronRight, ArrowUpRight, Edit, Reply
} from "lucide-react";
import { EmailCarousel } from "../email/EmailCarousel";
import { UnifiedEmailComposer } from "../email/UnifiedEmailComposer";
import { DocumentPreview } from "../DocumentPreview";
import { cn } from "../../lib/utils";

type ContextContent = {
  type: 'emails' | 'documents' | 'web_search' | 'email_composer' | 'email_reply' | 'none';
  data?: any;
  title?: string;
};

interface SideComposerProps {
  contextContent?: ContextContent;
  onCloseContext?: () => void;
  onReplyEmail?: (email: any) => Promise<void>;
  onGenerateEmailReply?: (email: any) => Promise<string>;
  onSendEmail?: (email: any) => Promise<void>;
  onAIModifyEmail?: (instruction: string, currentEmail: any) => Promise<any>;
  mode?: 'overlay' | 'inline';
}

// Helper functions
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

function WebSearchContent({ data }: { data: any }) {
  if (!data?.results?.length) {
    return (
      <div className="p-6 text-center text-slate-400">
        <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No search results available</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="space-y-2">
        {data.results.map((result: any, index: number) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.03 }}
            className="group bg-slate-800/50 hover:bg-slate-800/80 rounded-lg p-3 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-200"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2 bg-green-500/10 rounded-lg">
                <Globe className="w-4 h-4 text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h5 className="text-slate-200 font-medium text-sm line-clamp-2 group-hover:text-white transition-colors mb-1">
                  {result.title || 'Untitled'}
                </h5>
                {result.snippet && (
                  <p className="text-slate-400 text-xs line-clamp-2 mb-2">
                    {result.snippet}
                  </p>
                )}
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <ExternalLink className="w-3 h-3" />
                  <span className="truncate">{result.url}</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-slate-300 transition-colors" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function DocumentsContent({ data, onClose }: { data: any, onClose?: () => void }) {
  // Handle memory sources
  if (data?.isMemorySource && data?.sources) {
    return (
      <div className="p-4">
        <div className="space-y-3">
          {data.sources.map((source: any, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-purple-500/10 rounded-lg">
                  <User className="w-4 h-4 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="text-slate-200 font-medium text-sm mb-2">
                    {source.memory_type || 'Memory'}
                  </h5>
                  <p className="text-slate-300 text-xs mb-2 leading-relaxed">
                    {source.snippet || source.relevance || 'Memory content'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    {source.timestamp && (
                      <>
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(source.timestamp)}</span>
                      </>
                    )}
                    {source.relevance && (
                      <>
                        <span>•</span>
                        <span>{source.relevance}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Handle regular documents
  if (!data?.documents?.length) {
    return (
      <div className="p-6 text-center text-slate-400">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No documents available</p>
      </div>
    );
  }

  // Single preview view
  const doc = data.documents[0];
  const download = () => {
    if (doc?.url) window.open(doc.url, '_blank'); // rudimentary download/open; Drive direct download could be added
  };
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-slate-700/60">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 bg-blue-500/20 rounded flex items-center justify-center">
            <FileText className="w-3.5 h-3.5 text-blue-400" />
          </div>
          <div className="truncate text-slate-200 text-sm">{doc.name || 'Document'}</div>
              </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-2 py-1 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">Close</button>
          {doc.url && (
            <button onClick={() => window.open(doc.url, '_blank')} className="px-2 py-1 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">Open</button>
          )}
          <button onClick={download} className="px-2 py-1 text-xs rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">Download</button>
                </div>
              </div>
      <div className="flex-1 overflow-y-auto p-3">
        {doc.preview ? (
          <div className="bg-slate-900 border border-slate-800 rounded-md p-3 text-xs text-slate-300 whitespace-pre-wrap">
            {doc.preview}
              </div>
        ) : (
          <div className="text-slate-500 text-sm">No preview available</div>
        )}
      </div>
    </div>
  );
}

function EmailsContent({ 
  data, 
  onReplyEmail, 
  onSendEmail, 
  onAIModifyEmail 
}: { 
  data: any, 
  onReplyEmail?: (email: any) => Promise<void>,
  onSendEmail?: (email: any) => Promise<void>,
  onAIModifyEmail?: (instruction: string, currentEmail: any) => Promise<any>
}) {
  console.log('📧 [EmailsContent] Called with data:', data);
  console.log('📧 [EmailsContent] data.emails:', data?.emails);
  console.log('📧 [EmailsContent] emails length:', data?.emails?.length);
  const respondedIds: Set<string> | undefined = (window as any).__respondedEmailIds;
  
  if (!data?.emails?.length) {
    console.log('📧 [EmailsContent] No emails to display - data:', data);
    return (
      <div className="p-6 text-center text-slate-400">
        <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No emails available</p>
      </div>
    );
  }

  console.log('📧 [EmailsContent] Rendering', data.emails.length, 'emails');
  return (
    <div className="p-4">
      <EmailCarousel
        emails={data.emails}
        onReply={onReplyEmail}
        onSendEmail={onSendEmail}
        onAIModifyEmail={onAIModifyEmail}
        className="border-0 bg-transparent transform scale-90 origin-top"
        respondedIds={respondedIds}
      />
    </div>
  );
}

export function SideComposer({
  contextContent = { type: 'none' },
  onCloseContext,
  onReplyEmail,
  onGenerateEmailReply,
  onSendEmail,
  onAIModifyEmail,
  mode = 'overlay'
}: SideComposerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  console.log('📧 [SideComposer] Rendered with contextContent:', contextContent);

  // Auto-expand when content is available
  useEffect(() => {
    console.log('📧 [SideComposer] Effect triggered - contextContent.type:', contextContent.type);
    if (contextContent.type !== 'none') {
      console.log('📧 [SideComposer] Setting expanded to true');
      setIsExpanded(true);
    }
  }, [contextContent.type]);

  const handleClose = () => {
    setIsExpanded(false);
    if (onCloseContext) {
      onCloseContext();
    }
  };

  if (!isExpanded || contextContent.type === 'none') {
    return null;
  }

  const getIcon = () => {
    switch (contextContent.type) {
      case 'emails': return Mail;
      case 'documents': return FileText;
      case 'web_search': return Globe;
      case 'email_composer': return Edit;
      case 'email_reply': return Reply;
      default: return Search;
    }
  };

  const getTitle = () => {
    return contextContent.title || 'Content';
  };

  const renderContent = () => {
    switch (contextContent.type) {
      case 'emails':
        return (
          <EmailsContent 
            data={contextContent.data}
            onReplyEmail={onGenerateEmailReply as any}
            onSendEmail={onSendEmail}
            onAIModifyEmail={onAIModifyEmail}
          />
        );
      case 'documents':
        return <DocumentsContent data={contextContent.data} onClose={handleClose} />;
      case 'web_search':
        return <WebSearchContent data={contextContent.data} />;
      case 'email_composer':
        return (
          <div className="p-4">
            <UnifiedEmailComposer
              initialTo={contextContent.data?.to || ''}
              initialSubject={contextContent.data?.subject || ''}
              initialContent={contextContent.data?.content || ''}
              onSend={async (email) => {
                if (onSendEmail) {
                  await onSendEmail(email);
                }
                handleClose();
              }}
              onAIModify={onAIModifyEmail}
              onClose={handleClose}
              className="bg-slate-800/30 border-slate-600/30"
            />
          </div>
        );
      case 'email_reply':
        return (
          <div className="p-4">
            <UnifiedEmailComposer
              initialTo={contextContent.data?.to || ''}
              initialSubject={contextContent.data?.subject || ''}
              initialContent={contextContent.data?.content || ''}
              onSend={async (email) => {
                if (onSendEmail) {
                  await onSendEmail(email);
                }
                handleClose();
              }}
              onAIModify={onAIModifyEmail}
              onClose={handleClose}
              className="bg-slate-800/30 border-slate-600/30"
            />
          </div>
        );
      default:
        return (
          <div className="p-6 text-center text-slate-400">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No content to display</p>
          </div>
        );
    }
  };

  const Icon = getIcon();

  // Container styles depend on mode
  const containerClass = mode === 'inline'
    ? "relative h-full w-full bg-slate-800 border-l border-slate-700 flex flex-col"
    : "fixed top-0 right-0 h-full w-[600px] bg-slate-800 border-l border-slate-700 shadow-2xl z-40 flex flex-col";

  const shouldHideHeader = mode === 'inline' && (
    contextContent.type === 'emails' ||
    contextContent.type === 'email_reply' ||
    contextContent.type === 'email_composer'
  );

  return (
    <motion.div
      initial={mode === 'overlay' ? { x: '100%' } : { opacity: 0 }}
      animate={mode === 'overlay' ? { x: 0 } : { opacity: 1 }}
      exit={mode === 'overlay' ? { x: '100%' } : { opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className={containerClass}
    >
      {/* Header (hidden for inline emails/composer) */}
      {!shouldHideHeader && (
      <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center">
            <Icon className="w-4 h-4 text-blue-400" />
          </div>
          <h2 className="text-slate-200 font-medium text-sm">{getTitle()}</h2>
        </div>
        <button
          onClick={handleClose}
          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      )}

      {/* Content */}
      <div className={cn("flex-1 overflow-y-auto", shouldHideHeader ? "p-0" : "")}>
        {renderContent()}
      </div>
    </motion.div>
  );
}

// Keep the old export name for compatibility
export { SideComposer as SideTabPanel }; 